import { Injectable } from '@angular/core';
import {
    CookieService, CORE_CONFIG, EventService, HttpService, LazyloadService, LocalStorageService, SessionStorageService,
    STORAGE_KEY as CORE_STORAGE_KEY,
    UtilService,
} from '@dm/core';
import { syncSha256 } from '@dm/web-crypto-utils';
import { type SamlMockInputType, type SamlPostParams } from 'app/feature/auth/auth.enum';
import { ServerTypeE, SsoLogoutJumpMode } from 'app/feature/settings/users-security/sso-server/sso-server.const';
import { USER_ROLE_E } from 'app/feature/settings/users-security/user-role/user-role.enum';
import { QUICKSTARTJSON } from 'app/feature/share/global-search/global-infigo/const';
import { type Observable, type Subscription, fromEvent } from 'rxjs';
import { debounceTime, finalize, tap } from 'rxjs/operators';

import { LogoutTipComponent } from '../components/logout-tip/logout-tip.component';
import { USER_SCOPE } from '../constants';
import { EventServiceKeyE } from '../constants/event-service-key';
import { PreferenceModeE } from '../constants/simple-mode.const';
import { LANGUAGE_MATCH, PATH_MAP, PathTypeE, SESSION_KEY } from '../constants/sso.const';
import { type KeyValue } from '../types/key-value';
import { type LocalStorageEvent, type UserSecurityPolicy } from '../types/mo';
import { COOKIE_NO_NEED_CLEAR_WHITELIST, HASH_USER_ID_PREFIX, HIDE_ANOMALY_TIP, STORAGE_KEY } from './../constants/index';

export interface UserInfo {
    // 状态码
    accountstate?: number;
    deviceid?: string;
    iBaseToken?: string;
    lastloginip?: string;
    lastlogintime?: string;
    pwdchangetime?: number;
    roleId?: string;
    usergroup?: string;
    userid?: string;
    // 租户ID
    vstoreId?: string;
    // 租户NAME
    vstoreName?: string;
    username?: string;
    // 登录方式
    userscope?: string | number;
    // 鉴权模式
    commandauthmode?: string;
    /**
     * 用户偏好模式
     */
    preferenceMode?: PreferenceModeE;
    /** 用户所属SSO服务器类型 */
    ssoServiceType?: ServerTypeE;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    // 初始化信息是否加载
    initInfoLoaded = false;

    // 默认超时时间：30分钟
    private DEFAULT_EXPIRES_TIME = 30;
    // 用户信息
    private userInfo: UserInfo = {};
    // 会话过期时间
    private expireTime: number;
    // 是否已处于登出过程中
    private isLogouting = false;
    // 是否已显示会话失效倒计时
    private isSessionTimeoutShown = false;
    // 是否对超时检测进行例外
    private isTimeoutExcept = false;
    // SSO退出后跳转地址
    private logoutUrl: string;
    // SAML协议退出时应下发参数
    private samlPostParams: SamlMockInputType[];

    // 屏幕鼠标事件
    private mousemoveSub$: Subscription;
    // 定时检查用户操作
    private userEventIntervalDestroyFn: () => void;
    // 用户空闲时间，根据这个值判定是否会话超时
    private userIdleTime = 0;
    /**
     * 是否显示极简/标准模式切换Tip
     */
    private showSimpleModeTip: boolean;
    /**
     * 偏好模式（登录获取）
     */
    private preferenceMode = PreferenceModeE.Standard;

    constructor(
        private cookieService: CookieService,
        private httpService: HttpService,
        private eventService: EventService,
        private lazyLoadService: LazyloadService,
        private utilService: UtilService,
        private localStorageService: LocalStorageService,
        private sessionStorageService: SessionStorageService,
    ) {
        // 订阅退出登录事件
        this.eventService.subscribe('logout', (data) => {
            // 如果是在SAML协议超时退出期间  确保请求获取到返回值后再退出
            if (this.isLogouting && Number(this.userInfo?.ssoServiceType) === ServerTypeE.SAML) {
                return;
            }

            if (data && data.sendDeleteSession) {
                this.logout();
            } else {
                this.doLogout();
            }
        });

        // 订阅会话超时事件，弹出提示框
        this.eventService.subscribe('session_timeout', () => {
            this.disposeSession('timeout');
        });

        // 订阅会话失效事件，弹出提示框
        this.eventService.subscribe('session_invalid', () => {
            this.disposeSession('invalid');
        });

        // 安全策略信息更新，设置会话超时时间
        this.eventService.subscribe('userSecurityPolicyUpdate', (data: UserSecurityPolicy) => {
            this.setExpireTime(Number(data.SESSIONEXPIREDTIME));
        });

        // 登录后触发检查会话过期
        this.eventService.subscribe('checkSessionExpired', () => {
            this.doSessionCheck();
        });
        // 进入系统立即触发检查一次
        this.doSessionCheck();

        // 监听超时检测例外事件
        this.eventService.subscribe('system.disableTimeoutExcept', () => {
            this.setUserIdleTime(0);
        });

        // 监听刷新用户会话空闲时间事件
        this.eventService.subscribe('refreshIdleTime', () => {
            this.userIdleTime = 0;
            this.setUserIdleTime(0);
        });
    }

    /**
     * 清除波动检测页上tip是否隐藏的标志
     */
    clearAnomalyTipInfo(): void {
        if (this.localStorageService.get(HIDE_ANOMALY_TIP)) {
            this.localStorageService.remove(HIDE_ANOMALY_TIP);
        }
    }

    /**
     * 清除阵列是否部署防勒索应用标志
     */
    clearAntiransomwareFlag(): void {
        if (this.localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK)) {
            this.localStorageService.remove(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK);
        }
    }

    /**
     * 将会话信息写入cookie
     * @param userInfo 用户信息
     * @memberof AuthService
     */
    setLoginInfo(inputUserInfo: UserInfo): void {
        const userInfo = inputUserInfo || {};
        this.userInfo = userInfo || {};
        this.eventService.emit(EventServiceKeyE.USER_INFO_CHANGED);
        this.cookieService.set(CORE_CONFIG.tokenKey, userInfo.iBaseToken);
        this.cookieService.set(CORE_CONFIG.deviceIdKey, userInfo.deviceid);
    }

    /**
     * 将Localstorage用户偏好中的用户名进行匿名化
     * @returns
     */
    hashAllLocalStoraeUserId(): void {
        const hasHashed = this.localStorageService.get(STORAGE_KEY.USER_NAME_HASHED_FLAG);
        if (hasHashed) {
            return;
        }

        // 设置标记位，避免重复执行
        this.localStorageService.set(STORAGE_KEY.USER_NAME_HASHED_FLAG, true);

        // 将涉及用户名且退出后需要继续保存的用户偏好信息中的用户名进行匿名化
        this.hashUserIdItem(CORE_STORAGE_KEY.COLUMN_FILTER_STATUS);
        this.hashUserIdItem(STORAGE_KEY.QUICK_START_OPEN);
        this.hashUserIdItem(STORAGE_KEY.HOME_QUICK_START_OPEN);
        this.hashUserIdItem(STORAGE_KEY.SIMPLE_MOD);
    }

    /**
     * 指定key值进行Localstorage中的用户名匿名化
     * @param key 本地存储的key
     * @returns
     */
    hashUserIdItem(key: string): void {
        const map: KeyValue[] = this.localStorageService.get(key);
        if (!map) {
            return;
        }

        const userIds = Object.keys(map);
        if (!userIds || !userIds.length) {
            return;
        }

        userIds.forEach((userId) => {
            const val = map[userId];

            if (!val) {
                return;
            }

            // 删除原数据
            delete map[userId];

            // 将用户名匿名化后重新存入
            const hashName = this.getHashUserId(userId);
            map[hashName] = val;
        });

        // 更新到Localstorage中
        this.localStorageService.set(key, map);
    }

    /**
     * 获取匿名化后的用户名，先sha256哈希之后再截取后10位
     * @param str
     * @returns
     */
    getHashUserId(str: string): string {
        const shaStr = HASH_USER_ID_PREFIX + (syncSha256(str)).slice(-10);

        return shaStr;
    }

    /**
     * 将username写入localstorage并监听storage
     */
    storageListening = (e: LocalStorageEvent): void => {
        if (e.key === 'userName') {
            // 解决ie11初始化密码用户退出问题
            if (e.oldValue && e.newValue !== e.oldValue) {
                window.location.reload();
            }
        }
    };

    setUserName(inputUserInfo: UserInfo): void {
        const userInfo = inputUserInfo || {};
        if (userInfo.username) {
            // 同浏览器其他用户登录刷新本页面
            this.localStorageService.set('userName', userInfo.username);
            window.addEventListener('storage', this.storageListening);
        }
    }

    /**
     * 清除cookie中的会话信息,与本地缓存中的因子信息
     */
    clearLoginInfo(): void {
        this.cookieService.remove(CORE_CONFIG.tokenKey);
        this.cookieService.remove(CORE_CONFIG.deviceIdKey);
        this.localStorageService.remove('isTwoFactor');
        this.localStorageService.remove('isTwoFactorPass');
        this.disableTimeoutExcept();
    }

    /**
     * 清除localstorage中的username
     */
    clearUserName(): void {
        this.localStorageService.remove('userName');
    }

    /**
     * 清除localstorage中的homeQuickStartOpen
     */
    clearQuickStartFlag(): void {
        this.localStorageService.remove('homeQuickStartOpen');
    }

    /**
     * 清除localstorage中的task缓存
     */
    clearTasks(): void {
        this.localStorageService.remove(STORAGE_KEY.TASKS);
    }

    /**
     * 设置会话过期时间
     * @param time 会话超时时间（分钟）
     */
    setExpireTime(time: number): void {
        this.expireTime = time;
    }

    /**
     * 获取会话过期时间
     */
    getExpireTime(): number {
        return this.expireTime || this.DEFAULT_EXPIRES_TIME;
    }

    /**
     * 获取用户信息
     */
    getUserInfo(): UserInfo {
        return { ...this.userInfo };
    }

    /**
     * 获取roleId
     */
    getRoleId(): string {
        return this.userInfo?.roleId;
    }

    /**
     * 获取租户ID
     */
    getVstoreId(): string {
        return this.userInfo?.vstoreId;
    }

    /**
     * 获取租户NAME
     */
    getVstoreName(): string {
        return this.userInfo?.vstoreName;
    }

    /**
     * 获取用户名
     */
    getUserName(): string {
        return this.userInfo?.username;
    }

    /**
     * 获取设备鉴权模式
     */
    getAuthMode(): string {
        return this.userInfo?.commandauthmode;
    }

    /**
     * 判断是否鉴权成功
     */
    isAuth(): boolean {
        const isTokenPass = Boolean(this.cookieService.getToken());
        const isTwoFactor = this.localStorageService.get('isTwoFactor');
        const isTwoFactorPass = this.localStorageService.get('isTwoFactorPass');
        const isAuthPass = isTokenPass && ((isTwoFactor && isTwoFactorPass) || !isTwoFactor);

        return isAuthPass;
    }

    /**
     * 是否是远程协助用户
     */
    isRemoteAssistanceUser(): boolean {
        return Number(this.getRoleId()) === USER_ROLE_E.Remote_assistance_administrator;
    }

    /**
     * 获取超时检测例外状态
     */
    getTimeoutExcept(): boolean {
        const cookieFlag = this.cookieService.get('isTimeoutExcept');
        return this.isTimeoutExcept || Boolean(cookieFlag);
    }

    /**
     * 开启超时检测例外状态，会话超时检测功能将失效
     */
    enableTimeoutExcept(): void {
        this.isTimeoutExcept = true;
        this.cookieService.set('isTimeoutExcept', 'true');
        this.eventService.emit('system.enableTimeoutExcept');
    }

    /**
     * 禁用超时检测例外状态，会话超时检测功能将生效
     */
    disableTimeoutExcept(): void {
        this.isTimeoutExcept = false;
        this.cookieService.remove('isTimeoutExcept');
        this.eventService.emit('system.disableTimeoutExcept');
    }

    /**
     * 读取cookie中的用户闲置时间
     */
    getUserIdleTime(): number {
        return Number(this.cookieService.get('userIdleTime')) || 0;
    }

    /**
     * 将用户闲置时间写入cookie
     * @param userIdleTime 用户闲置时间
     */
    setUserIdleTime(userIdleTime: number): void {
        this.cookieService.set('userIdleTime', userIdleTime);
    }

    /**
     * 从后端获取登录信息
     */
    initLoginInfo(): Observable<any> {
        return this.httpService.get(this.httpService.makeUrl('server.loginInfo'))
            .pipe(
                tap(({ data }) => {
                    this.userInfo = data || {};
                    this.eventService.emit(EventServiceKeyE.USER_INFO_CHANGED);
                    this.cookieService.set(CORE_CONFIG.deviceIdKey, this.userInfo.deviceid);
                }),
            );
    }

    /**
     * 发出退出登录请求
     * @param showMask 是否显示忙等框（默认显示）
     */
    logout(showMask = true): void {
        this.isLogouting = true;
        // sso用户退出登录
        if (Number(this.userInfo.userscope) === USER_SCOPE.SSO) {
            this.ssoLogout(showMask);
        } else {
            this.normalLogout(showMask);
        }
    }

    /**
     * 识别FCV路径参数
     */
    convertParams(): void {
        // 先清除SesstionStorage里存储的值
        this.sessionStorageService.remove(SESSION_KEY);

        const url = window.location.href;

        // 如果传了语言，需要切换到对应的语种
        const languageIndex = url.indexOf(LANGUAGE_MATCH);
        const language = window.location.href.slice(languageIndex + LANGUAGE_MATCH.length);

        // 判断语言是否为中文或者英文，避免不传、传空值或者错误值
        const isLanguageValid = language === 'zh' || language === 'en';
        if (languageIndex !== -1 && isLanguageValid) {
            // 从本地存储取语言旧值
            const oldLanguage = this.localStorageService.get(CORE_CONFIG.languageKey);

            // 和本地不一致才存localStorage，并且刷新页面，不然会循环引用
            if (oldLanguage !== language) {
                this.localStorageService.set(CORE_CONFIG.languageKey, language);
                window.location.reload();
            }
        }

        if (url.includes(PathTypeE.File)) {
            this.sessionStorageService.set(SESSION_KEY, PathTypeE.File);
        } else if (url.includes(PathTypeE.ASSETS)) {
            this.sessionStorageService.set(SESSION_KEY, PathTypeE.ASSETS);
        } else {
            //
        }
    }

    /**
     * 获取精确跳转路径
     */
    getTargetPath(): string {
        let targetPath = '';
        const pathType: PathTypeE = this.sessionStorageService.get(SESSION_KEY);
        if (!pathType) {
            return targetPath;
        }

        targetPath = PATH_MAP[pathType];

        // 新手引导路径拼接
        if (pathType === PathTypeE.ASSETS) {
            targetPath += QUICKSTARTJSON(this.localStorageService.get(CORE_CONFIG.languageKey), false)[1].url;
        }

        return targetPath;
    }

    /**
     * 用户登录后默认显示一次极简/标准模式切换Tip
     */
    getShowSimpleModeTip(): boolean {
        return Boolean(this.showSimpleModeTip);
    }

    /**
     * 设置是否显示极简/标准模式切换Tip
     */
    setShowSimpleModeTip(status: boolean): void {
        this.showSimpleModeTip = status;
    }

    /**
     * 设置用户偏好模式（登录成功后保存接口里的偏好模式）
     * @param mode 用户偏好模式
     * @param userId 用户id
     */
    setPreferenceMode(mode: PreferenceModeE): void {
        if (mode === undefined) {
            return;
        }
        this.preferenceMode = mode;
    }

    /**
     * 获取用户偏好模式
     */
    getPreferenceMode(): PreferenceModeE {
        return this.preferenceMode;
    }


    /**
     * 释放会话
     * @param type 退出登录类型。timeout：超时退出。invalid：会话失效退出
     */
    private disposeSession(type: 'timeout' | 'invalid'): void {
        // 如果是在SAML协议超时退出期间  确保请求获取到返回值后再退出
        if (this.isLogouting && Number(this.userInfo?.ssoServiceType) === ServerTypeE.SAML) {
            return;
        }
        // 登出过程中触发的会话超时不提示，直接跳到登录页
        if (this.isLogouting) {
            this.doLogout();
            return;
        }

        if (this.isSessionTimeoutShown) {
            return;
        }

        if (type === 'timeout') {
            this.logout(false);
        }

        this.promptCountDown();
    }

    private promptCountDown(): void {
        if (this.isSessionTimeoutShown) {
            return;
        }

        this.lazyLoadService.appendComponentEnsure(LogoutTipComponent);
        this.isSessionTimeoutShown = true;
    }

    /**
     * 执行退出后页面跳转，采用强制刷新的机制，以释放内存
     */
    private doLogout(): void {
        this.clearLoginInfo();
        this.clearTasks();
        this.clearQuickStartFlag();
        this.cookieService.clear(COOKIE_NO_NEED_CLEAR_WHITELIST);
        this.sessionStorageService.remove('vstoreId');
        this.clearUserName();
        this.clearAnomalyTipInfo();
        this.clearAntiransomwareFlag();

        // 关闭storage事件监听
        window.removeEventListener('storage', this.storageListening);

        // 执行登出跳转过程中不再弹出倒计时提示框
        this.isSessionTimeoutShown = true;

        const gotoLoginUrl = (url): void => window.location.href = url;

        if (Number(this.userInfo.userscope) === USER_SCOPE.SSO) {
            if (this.logoutUrl) {
                // 如果是saml协议，执行特殊跳出逻辑，否则实行原逻辑
                if (this.samlPostParams?.length) {
                    this.mockhttpPost(this.logoutUrl, this.samlPostParams);
                } else {
                    gotoLoginUrl(this.logoutUrl);
                }
                return;
            }

            // saml协议退回到DM登录界面
            if (Number(this.userInfo.ssoServiceType) === ServerTypeE.SAML) {
                gotoLoginUrl('/login');
                return;
            }

            const returnUrl = `${location.origin}/login/sso`;
            gotoLoginUrl(returnUrl);
        } else {
            gotoLoginUrl('/login');
        }
    }

    /**
     * 普通用户退出
     */
    private normalLogout(showMask: boolean): void {
        const rest = this.httpService.makeUrl('user.logout');

        // 不显示忙等框表示是超时退出，此时需要下发is_timeout=1
        if (!showMask) {
            rest.setParams({ is_timeout: 1 });
        }

        this.httpService.delete(rest, { mask: showMask })
            .pipe(finalize(() => this.doLogout()))
            .subscribe();
    }

    /**
     * SSO用户退出
     */
    private ssoLogout(showMask): void {
        const rest = this.httpService.makeUrl('sso.logout');
        this.httpService.post(rest, null, { mask: showMask }).subscribe(
            (res) => {
                const redirectUrl = res?.data?.redirectUrl;
                if (res.data?.samlRequest) {
                    // 如果是smal协议的SSO流程  构建SMAL协议模拟表单
                    this.getSsoSMALForm(res.data);
                }

                if (redirectUrl) {
                    this.logoutUrl = `${redirectUrl}?service=${location.origin}/login/sso`;

                    if (Number(res.data?.ssoMode) === SsoLogoutJumpMode.NOTJUMP) {
                        this.logoutUrl = `${redirectUrl}?service=${location.origin}/deviceManager/rest/xxx/sso_user_login`;
                    }
                }

                this.doLogout();
            },
            // SSO退出失败时用普通退出补救
            () => {
                this.normalLogout(showMask);
            },
        );
    }

    /**
     * SAML协议下的特殊参数处理方式
     * @param data sso_user_login回调参数
     */
    private getSsoSMALForm(data: SamlPostParams): void {
        this.samlPostParams = [
            { name: 'SAMLRequest', value: data.samlRequest },
            { name: 'sigAlg', value: data.sigAlg },
            { name: 'signature', value: data.signature },
            { name: 'RelayState', value: data.RelayState },
            { name: 'managementIPAddress', value: data.managementIPAddress },
        ];
    }

    /**
     * SAML协议下模拟表单重定向 携带SSO下发所需参数
     * @param params SAML协议所需参数
     * @param url 重定向地址
     */
    private mockhttpPost(url: string, params: SamlMockInputType[]): HTMLFormElement {
        const formElement = document.createElement('form');
        formElement.action = url;
        formElement.method = 'post';
        formElement.style.display = 'none';
        formElement.target = '_self';
        for (const i of params) {
            if (this.utilService.encodeHTML(i.value)) {
                const tmpChild = document.createElement('input');
                tmpChild.name = this.utilService.encodeHTML(i.name);
                tmpChild.value = this.utilService.encodeHTML(i.value);
                formElement.appendChild(tmpChild);
            }
        }
        document.body.appendChild(formElement);
        formElement.submit();
        return formElement;
    }

    /**
     * 检测会话超时
     */
    private doSessionCheck(): void {
        if (!this.isAuth()) {
            return;
        }

        if (this.mousemoveSub$) {
            this.mousemoveSub$.unsubscribe();
        }

        this.mousemoveSub$ = fromEvent(document, 'mousemove')
            .pipe(debounceTime(500))
            .subscribe(() => {
                // 正常在线时，空闲时间重新计时
                this.userIdleTime = 0;
                this.checkWindowEvent();
            });

        // 立即检测一次
        this.checkWindowEvent();
    }

    /**
     * 检测空闲时间是否超过了会话限制
     */
    private checkWindowEvent(): void {
        // 秒单位值
        const sUnit = 1e3;
        // 分钟单位换算值
        const mUnit = 60 * sUnit;
        // 每5s去检测一次浏览器的操作状态
        const checkTime = 5 * sUnit;

        if (this.userEventIntervalDestroyFn) {
            this.userEventIntervalDestroyFn();
        }

        this.userEventIntervalDestroyFn = this.utilService.interval(() => {
            // 会话过期分钟数，每次检查获取最新的会话过期时间
            const gap = this.getExpireTime();
            // 如果超时检测例外开启，则超时时间至少为60分钟
            const effectGap = this.getTimeoutExcept() ? Math.max(gap, 60) : gap;
            // 用户会话操作间隔最大值
            const gapTime = effectGap * mUnit;

            // 如果从cookie中读取到更小的用户闲置时间，说明有其他未闲置页面，当前页面也使用该较小值
            const cookieUserIdleTime = this.getUserIdleTime();
            if (cookieUserIdleTime < this.userIdleTime) {
                this.userIdleTime = cookieUserIdleTime;
            }

            this.userIdleTime += checkTime;
            this.setUserIdleTime(this.userIdleTime);

            if (this.userIdleTime < gapTime) {
                return;
            }

            this.eventService.emit('session_timeout');
        }, checkTime);
    }
}
